import { alpha, type Theme } from "@mui/material/styles";
import type { AppDocsPageStyles } from "../types";

export function buildDocsPageStyles(theme: Theme): AppDocsPageStyles {
  const tocBg = alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.08 : 0.04
  );

  const codeBg = alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.12 : 0.06);

  return {
    shell: {
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: "flex-start",
      gap: { xs: 1.5, md: 2 },
      minWidth: 0,
    },
    toc: {
      width: { xs: "100%", md: 220 },
      flexShrink: 0,
      position: { md: "sticky" },
      top: { md: 72 },
      // Stay within the viewport under the sticky navbar so long menus can scroll.
      maxHeight: { xs: "none", md: "calc(100vh - 88px)" },
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflow: "hidden",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.25,
      bgcolor: tocBg,
      p: 1.25,
    },
    tocTitle: {
      fontWeight: 700,
      px: 1,
      mb: 0.75,
      flexShrink: 0,
    },
    tocList: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      pr: 0.5,
      WebkitOverflowScrolling: "touch",
    },
    tocGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
    },
    tocGroupLabel: {
      px: 1,
      pt: 0.5,
      pb: 0.25,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
    },
    tocLink: {
      display: "block",
      px: 1,
      py: 0.75,
      borderRadius: 1,
      color: "text.secondary",
      textDecoration: "none",
      fontSize: 14,
      lineHeight: 1.4,
      "&:hover": {
        bgcolor: "action.hover",
        color: "text.primary",
      },
    },
    tocLinkActive: {
      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
      color: "primary.main",
      fontWeight: 600,
      "&:hover": {
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.24 : 0.16),
        color: "primary.main",
      },
    },
    tocLinkNested: {
      display: "block",
      pl: 2.5,
      pr: 1,
      py: 0.5,
      borderRadius: 1,
      color: "text.secondary",
      textDecoration: "none",
      fontSize: 13,
      lineHeight: 1.35,
      "&:hover": {
        bgcolor: "action.hover",
        color: "text.primary",
      },
    },
    tocLinkNestedActive: {
      color: "primary.main",
      fontWeight: 600,
    },
    article: {
      flex: 1,
      minWidth: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      bgcolor: "background.paper",
      p: { xs: 2, md: 3 },
    },
    markdown: {
      color: "text.primary",
      "& h1": { ...theme.typography.h4, fontWeight: 700, mt: 0, mb: 2 },
      "& h2": { ...theme.typography.h5, fontWeight: 700, mt: 3.5, mb: 1.25 },
      "& h3": { ...theme.typography.h6, fontWeight: 600, mt: 2.5, mb: 1 },
      "& p, & li": { ...theme.typography.body1, color: "text.primary" },
      "& p": { mb: 1.5 },
      "& em": {
        color: "text.secondary",
        fontSize: 13,
        display: "block",
        mt: -0.5,
        mb: 2,
      },
      "& ul, & ol": { pl: 3, mb: 1.5 },
      "& li": { mb: 0.5 },
      "& hr": { borderColor: "divider", my: 3 },
      "& blockquote": {
        m: 0,
        mb: 2,
        pl: 2,
        borderLeft: "3px solid",
        borderColor: "primary.main",
        color: "text.secondary",
      },
      "& a": { color: "primary.main" },
      "& table": {
        width: "100%",
        borderCollapse: "collapse",
        mb: 2,
        fontSize: 14,
      },
      "& th, & td": {
        border: "1px solid",
        borderColor: "divider",
        p: 1,
        textAlign: "left",
        verticalAlign: "top",
      },
      "& th": {
        bgcolor: tocBg,
        fontWeight: 700,
      },
    },
    markdownImage: {
      display: "block",
      maxWidth: "100%",
      height: "auto",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      my: 2,
      bgcolor: "background.default",
    },
    markdownTableWrap: {
      overflowX: "auto",
      mb: 2,
    },
    markdownPre: {
      p: 1.5,
      borderRadius: 1.5,
      overflowX: "auto",
      bgcolor: codeBg,
      color: "text.primary",
      fontSize: 13,
      lineHeight: 1.6,
      mb: 2,
    },
    markdownCode: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: "0.875em",
      px: 0.5,
      py: 0.15,
      borderRadius: 0.5,
      bgcolor: codeBg,
      color: "text.primary",
    },
  };
}
